/**
 * "Side-by-side run" on a project's Diurnal QC tab (docs/specs/cloud/diurnal-qc-cloud.md §6; Diurnal inventory 02 §9
 * "Parallel running"; decision 111: no retirement date, MB phases the Diurnal tool out when he is ready). A person
 * chooses a day and base station Teslyn Cloud worked out, and the exceedance workbook the Diurnal tool (Excel
 * Exceedance Report, Ctrl+J) or Teslyn Field wrote for the same TA Live day; the page compares each exceedance run with
 * the workbook's rows (`compareSideBySide`) and the person records the comparison with a note. The list of days
 * compared is what MB signs the side-by-side run off from.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  SIDE_BY_SIDE_SOURCE_LABEL,
  canAcknowledgeNotice,
  cloudQcMetricWords,
  compareSideBySide,
  readExceedanceWorkbook,
  runClock,
  sideBySideSummary,
  sodClock,
  xlsxRows,
  XlsxReadError,
  type CloudQcDayRow,
  type SideBySideResult,
  type SideBySideSource,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState } from '../components/States.tsx';
import type { PortalProject } from './api.ts';
import {
  browserInflateRaw,
  deleteComparison,
  loadComparisons,
  saveComparison,
  type Comparison,
} from './diurnalApi.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const VERDICT_WORDS: Readonly<Record<string, string>> = {
  agrees: 'Agrees',
  times_differ: 'Times differ',
  cloud_only: 'Only in Teslyn Cloud',
};

export interface SideBySidePanelProps {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly days: readonly CloudQcDayRow[];
  readonly roles: readonly string[];
  /** Tests pass their own inflater. */
  readonly inflate?: (data: Uint8Array) => Promise<Uint8Array>;
}

export function SideBySidePanel({
  sb,
  project,
  days,
  roles,
  inflate = browserInflateRaw,
}: SideBySidePanelProps): ReactElement {
  const mayRecord = canAcknowledgeNotice(roles) && project.status !== 'archived';
  const [list, setList] = useState<Comparison[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dayId, setDayId] = useState<string>(days[0]?.id ?? '');
  const [source, setSource] = useState<SideBySideSource>('diurnal_tool');
  const [file, setFile] = useState<{ name: string; result: SideBySideResult } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound((r) => r + 1), []);
  const day = days.find((d) => d.id === dayId) ?? days[0] ?? null;

  useEffect(() => {
    let on = true;
    loadComparisons(sb, project.id)
      .then((l) => on && setList(l))
      .catch((e: unknown) => on && setError(message(e)));
    return () => {
      on = false;
    };
  }, [sb, project.id, round]);

  const names = useMemo(() => new Map(days.map((d) => [d.station_id, d.station_name])), [days]);

  async function read(f: File): Promise<void> {
    setProblem(null);
    setFile(null);
    setSaved(null);
    if (!day) return;
    try {
      const rows = await xlsxRows(new Uint8Array(await f.arrayBuffer()), inflate);
      const w = readExceedanceWorkbook(rows);
      if (!w.ok) {
        setProblem(w.message);
        return;
      }
      setFile({
        name: f.name,
        result: compareSideBySide({
          day: day.day,
          offsetH: day.offset_h,
          items: day.items,
          periods: w.periods,
        }),
      });
    } catch (e) {
      setProblem(e instanceof XlsxReadError ? e.message : `Teslyn could not read ${f.name}: ${message(e)}`);
    }
  }

  async function record(): Promise<void> {
    if (!file || !day) return;
    setBusy(true);
    setSaved(null);
    try {
      const { data } = await sb.auth.getSession();
      const by = data.session?.user.id;
      if (!by) throw new Error('Sign in again to record the comparison.');
      await saveComparison(sb, {
        tenant_id: project.tenantId,
        project_id: project.id,
        station_id: day.station_id,
        day: day.day,
        source,
        file_name: file.name.slice(0, 200),
        cloud_computed_at: day.computed_at,
        result: file.result,
        verdict: file.result.verdict,
        note: note.trim().slice(0, 1000),
        compared_by: by,
      });
      setSaved(`Recorded: ${day.station_name}, ${day.day}, ${sideBySideSummary(file.result)}`);
      setFile(null);
      setNote('');
      reload();
    } catch (e) {
      setProblem(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Comparison): Promise<void> {
    try {
      await deleteComparison(sb, c.id);
      reload();
    } catch (e) {
      setProblem(message(e));
    }
  }

  const same = (list ?? []).filter((c) => c.verdict === 'same').length;
  return (
    <div className="panel stack" aria-label="Side-by-side run">
      <h2 className="heading">Side-by-side run</h2>
      <p className="caption muted">
        Compare a day of Teslyn Cloud with the exceedance workbook the Diurnal tool (Excel Exceedance Report)
        or Teslyn Field wrote for the same TA Live day, with the same Diurnal QC values. The workbook’s rows
        are cut at survey lines, so a row agrees when it lies inside a Teslyn Cloud run and starts or ends
        with it; a run of one reading is left out, as the workbook leaves it out.
      </p>
      {mayRecord && day && (
        <div className="stack">
          <div className="row row--wrap">
            <label className="row">
              <span className="label">Day and station</span>
              <select value={day.id} onChange={(e) => setDayId(e.target.value)}>
                {days.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.day} · {d.station_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="row">
              <span className="label">Workbook from</span>
              <select value={source} onChange={(e) => setSource(e.target.value as SideBySideSource)}>
                {(Object.keys(SIDE_BY_SIDE_SOURCE_LABEL) as SideBySideSource[]).map((s) => (
                  <option key={s} value={s}>
                    {SIDE_BY_SIDE_SOURCE_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="row">
              <span className="label">Exceedance workbook (.xlsx)</span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void read(f);
                }}
              />
            </label>
          </div>
          {problem && (
            <p className="notice notice--fail" role="alert">
              {problem}
            </p>
          )}
          {file && (
            <div className="stack" aria-label="Comparison">
              <p className={file.result.verdict === 'same' ? 'notice' : 'notice notice--fail'} role="status">
                {sideBySideSummary(file.result)}
              </p>
              {file.result.runs.length + file.result.workbook_only.length > 0 && (
                <div className="table-wrap">
                  <table className="table" aria-label="Runs and workbook rows">
                    <thead>
                      <tr>
                        <th className="label">Teslyn Cloud run</th>
                        <th className="label">Metrics</th>
                        <th className="label">Workbook rows</th>
                        <th className="label">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {file.result.runs.map((r) => (
                        <tr key={r.start_ms}>
                          <td className="data">
                            {runClock(r.start_ms, day.offset_h)} to {runClock(r.end_ms, day.offset_h)}
                          </td>
                          <td>{cloudQcMetricWords(r.metrics)}</td>
                          <td className="data">
                            {r.rows
                              .map((p) => `${sodClock(p.start_sod)} to ${sodClock(p.end_sod)} (${p.line})`)
                              .join('; ') || '—'}
                          </td>
                          <td>{VERDICT_WORDS[r.verdict]}</td>
                        </tr>
                      ))}
                      {file.result.workbook_only.map((p) => (
                        <tr key={`w${p.start_sod}:${p.line}`}>
                          <td>—</td>
                          <td>{p.source}</td>
                          <td className="data">
                            {sodClock(p.start_sod)} to {sodClock(p.end_sod)} ({p.line})
                          </td>
                          <td>Only in the workbook</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <label className="stack">
                <span className="label">Note (what differs and why, if anything)</span>
                <textarea
                  aria-label="Note"
                  spellCheck
                  value={note}
                  maxLength={1000}
                  rows={2}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <div className="row">
                <Button busy={busy} onClick={() => void record()}>
                  Record this comparison
                </Button>
              </div>
            </div>
          )}
          {saved && (
            <p className="body" role="status">
              {saved}
            </p>
          )}
        </div>
      )}
      {error ? (
        <ErrorState message={error} />
      ) : list === null ? null : list.length === 0 ? (
        <p className="body muted">No day compared yet.</p>
      ) : (
        <>
          <p className="body">
            {list.length} day{list.length === 1 ? '' : 's'} compared, {same} the same.
          </p>
          <div className="table-wrap">
            <table className="table" aria-label="Days compared">
              <thead>
                <tr>
                  <th className="label">Day</th>
                  <th className="label">Station</th>
                  <th className="label">Workbook</th>
                  <th className="label">Result</th>
                  <th className="label">Note</th>
                  <th className="label" />
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id}>
                    <td className="data">{c.day}</td>
                    <td>{(c.station_id && names.get(c.station_id)) || 'Base station'}</td>
                    <td>
                      {SIDE_BY_SIDE_SOURCE_LABEL[c.source]}
                      <span className="caption muted"> {c.file_name}</span>
                    </td>
                    <td>{sideBySideSummary(c.result)}</td>
                    <td className="table__text">{c.note}</td>
                    <td className="table__actions">
                      {mayRecord && (
                        <Button variant="quiet" onClick={() => void remove(c)}>
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
